import { useRunState } from '@/src/game/provider/useRunState'
import { useRestartRun } from '@/src/hooks/useRestartRun'
import { observer } from 'mobx-react-lite'
import { AnimatePresence, motion } from 'motion/react'
import { useCallback, useState } from 'react'
import { useParentsAppointmentActions } from '@/src/hooks/useParentsAppointmentActions'
import Button from '../ui/buttons/Button'
import TextHeadline from '../ui/texts/TextHeadline'
import clsx from 'clsx'
import StatElement from '../ui/pauseElements/StatElement'
import StatModal, { StatModalContent } from '../ui/pauseElements/StatModal'
import UpgradeBar from '../ui/pauseElements/UpgradeBar'
import AppointmentSelectionModal from './AppointmentSelectionModal'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import { getLevelProgress, LEVELS } from '@/src/game/config/levels'
// import { GetTooltipsDocument } from '@/src/lib/graphql/generated/graphql'
import { store } from '@/src/stores/store'
import { playButtonPrimarySound, playPopupOpenSound } from '@/src/game/audio/uiSounds'
import TextDefault from '../ui/texts/TextDefault'
import IconRun from '../ui/icons/IconRun'
import IconPausePlain from '../ui/icons/IconPausePlain'

type StatId =
    | 'distance'
    | 'crashes'
    | 'shield'
    | 'boost'
    | 'battery'
    | 'coin'

// const tooltipData = store.getCachedQuery(GetTooltipsDocument);

const modalContentMap: Record<StatId, StatModalContent> = {
    distance: {
        title: 'Distanz',
        description: 'Wie weit bist du gekommen?',
        parentTitle: 'Für Eltern',
        parentDescription: 'Die Distanz zeigt, wie weit du bisher gelaufen bist. Je weiter du kommst, desto mehr Vorsorge-Münzen sammelst du.',
        color: 'blue'
    },
    crashes: {
        title: 'Krankenschutz',
        description: 'Mit einem Herz bekommst du eine weitere Chance.',
        parentTitle: 'Für Eltern',
        parentDescription: 'Hinter dem Herz steckt der Krankenschutz. Er hilft dabei, im Ernstfall die bestmögliche Versorgung und zusätzliche Unterstützung zu erhalten.',
        color: 'blue'
    },
    shield: {
        icon: '/images/powerups/PowerUp_Shield_Shift.png',
        title: 'Schutzschild',
        description: 'Schützt dich vor dem nächsten Hindernis!',
        parentTitle: 'Für Eltern',
        parentDescription: 'Hinter dem Schutzschild steckt der Grundfähigkeitsschutz. Er sichert die wichtigen Fähigkeiten Ihres Kindes finanziell ab.',
        color: 'blue'
    },
    boost: {
        icon: '/images/powerups/PowerUp_Star_Shift.png',
        title: 'Berufs-Boost',
        description: 'Werde für kurze Zeit unaufhaltbar!',
        parentTitle: 'Für Eltern',
        parentDescription: 'Hinter dem Berufs-Boost steckt die Berufsunfähigkeitsabsicherung. Sie hilft finanziell, wenn Ihr Kind später den Beruf krankheits- oder unfallbedingt nicht mehr ausüben kann.',
        color: 'orange'
    },
    battery: {
        icon: '/images/powerups/PowerUp_Batterie_Shift.png',
        title: 'Unfall-Blitz',
        description: 'Lade den Blitz auf und lass Hindernisse verschwinden!',
        parentTitle: 'Für Eltern',
        parentDescription: 'Hinter dem Unfall-Blitz steckt die Unfallabsicherung. Sie leistet nach unerwarteten Ereignissen schnelle finanzielle Unterstützung.',
        color: 'green'
    },
    coin: {
        icon: '/images/powerups/PowerUp_Coin_Shift.png',
        title: 'Vorsorge-Münzen',
        description: 'Sammle so viele Münzen wie möglich für den Highscore!',
        parentTitle: 'Für Eltern',
        parentDescription: 'Hinter den Vorsorge-Münzen steckt die Zukunftsvorsorge. Wer früh beginnt, gibt Zeit und Zinseszins die Chance, langfristig etwas Großes aufzubauen.',
        color: 'blue'
    },
}

const pauseContentVariants = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.35,
            when: 'beforeChildren' as const,
        },
    },
    exit: {
        opacity: 0,
        scale: 0.95,
        transition: { duration: 0.2, ease: 'easeIn' as const },
    },
}

const statsCardVariants = {
    hidden: { opacity: 0, scale: 0.7, y: -20 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: {
            type: 'spring' as const,
            stiffness: 380,
            damping: 18,
            mass: 0.8,
        },
    },
}

const drawerVariants = {
    hidden: {
        height: 0,
        opacity: 1,
    },
    visible: {
        height: 'auto' as const,
        transition: {
            type: 'spring' as const,
            stiffness: 220,
            damping: 22,
            mass: 1,
        },
    },
}


const drawerContentVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.25,
            duration: 0.3,
            ease: 'easeOut' as const,
        },
    },
}

type PauseScreenVariant = 'pause' | 'gameOver'

const MAX_UNLOCKS = 4

type PauseScreenProps = {
    variant?: PauseScreenVariant
    onResume?: () => void
}

const PauseScreen = observer(function PauseScreen({
    variant = 'pause',
    onResume,
}: PauseScreenProps) {
    const {
        mappingRows,
        showBranchPicker,
        setShowBranchPicker,
        openAppointment,
    } = useParentsAppointmentActions()

    const handleTerminVereinbaren = useCallback(() => {
        openAppointment()
    }, [openAppointment])

    const runState = useRunState()
    const restartRun = useRestartRun()
    const snapshot = runState.snapshot

    const [openStatId, setOpenStatId] = useState<StatId | null>(null)

    const openStatModal = useCallback((statId: StatId) => {
        playPopupOpenSound()
        setOpenStatId(statId)
    }, [])

    const closeStatModal = useCallback(() => {
        setOpenStatId(null)
    }, [])

    const handleResume = () => {
        playButtonPrimarySound()
        onResume?.()
    }

    const handleContinueOnPhone = () => {
        playButtonPrimarySound()
        store.setContinueOnPhoneOpen(true)
    }

    const handleRestart = () => {
        playButtonPrimarySound()
        restartRun()
    }

    const handleRestartToConfigurator = () => {
        playButtonPrimarySound()
        restartRun('configurator', { withResetTransition: true })
    }

    const handleReturnToStart = () => {
        playButtonPrimarySound()
        store.resetGame()
    }

    const distanceLabel = `${Math.floor(store.displayTrackDistance)} m`
    const totalLevelCount = LEVELS.length
    const { level, progress: levelSegmentProgress } = getLevelProgress(runState.points)
    const unlockedCount = Math.min(
        LEVELS.filter(({ end }) => runState.points >= end).length,
        MAX_UNLOCKS,
    )
    const upgradeBarProgress = Math.min(
        ((level - 1) + levelSegmentProgress) / totalLevelCount,
        1,
    )
    const secondary = [
        {
            id: 'distance',
            label: 'Distanz',
            value: distanceLabel,
            icon: '/images/powerups/Shoe.png',
            hasInfo: false
        },
        {
            id: 'shield',
            label: 'Schutzschild',
            value: snapshot.runEndStats.shieldPowerUpsCollected,
            icon: '/images/powerups/PowerUp_Shield_Shift.png',
            hasInfo: true
        },
        {
            id: 'battery',
            label: 'Unfall-Blitz',
            value: snapshot.runEndStats.batteryPowerUpsCollected,
            icon: '/images/powerups/PowerUp_Batterie_Shift.png',
            hasInfo: true
        },
        {
            id: 'boost',
            label: 'Berufs-Boost',
            value: snapshot.runEndStats.planePowerUpsCollected,
            icon: '/images/powerups/PowerUp_Star_Shift.png',
            hasInfo: true
        },
    ] as const satisfies ReadonlyArray<{
        id: StatId
        label: string
        value: number | string
        icon: string
        hasInfo: boolean
    }>

    const modalContent = openStatId ? modalContentMap[openStatId] : null

    const isStele = useSteleBreakpoint();

    return (
        <div
            className={clsx(
                'z-70 absolute inset-0 flex flex-col justify-center items-center bg-black/65 backdrop-blur-sm pointer-events-auto',
                'gap-4',
                'stele:gap-16'
            )}
        >
            {variant === 'pause' &&
                <div className='bottom-8 stele:bottom-64 absolute flex justify-center items-center gap-1.75 stele:gap-7 bg-white-gradient bg-clip-text drop-shadow-2-white-stroke text-transparent'>
                    <p className='font-grandstander font-bold text-[16px] stele:text-[64px] italic uppercase'>PAUSIERT</p>
                    <IconPausePlain width={isStele ? '64' : '16'} height={isStele ? '64' : '16'} />
                </div>
            }
            <AppointmentSelectionModal
                open={showBranchPicker}
                onClose={() => setShowBranchPicker(false)}
                mappingRows={mappingRows}
            />
            <AnimatePresence mode='wait'>
                {openStatId === null ? (
                    <motion.div
                        key='pause-content'
                        className='stele:bottom-128 absolute'
                        variants={pauseContentVariants}
                        initial='hidden'
                        animate='visible'
                        exit='exit'
                    >
                        <motion.div
                            className='z-40 relative flex flex-col items-center'
                            variants={statsCardVariants}
                        >
                            <div
                                className={clsx(
                                    'z-0 relative flex flex-col items-center bg-blue-gradient drop-shadow-2-white-stroke-darker border-white text-slate-900',
                                    'gap-4 p-4 pb-6 rounded-4xl max-w-md border-8 min-w-74',
                                    'stele:gap-16 stele:p-16 stele:pb-24 stele:rounded-[128px] stele:max-w-448 stele:border-32 stele:min-w-296',

                                )}>
                                <TextHeadline
                                    className={clsx(
                                        'absolute',
                                        '-top-15.5',
                                        'stele:-top-62'
                                    )}
                                    fontSize={isStele ? 192 : 48}
                                    leading={1}
                                    tracking={'-2%'}
                                    colorPreset='red'
                                >
                                    Dein Lauf!
                                </TextHeadline>
                                <div
                                    className={clsx(
                                        'grid grid-cols-2',
                                        'gap-x-3 gap-y-4',
                                        'stele:gap-x-12 stele:gap-y-12'
                                    )}
                                >
                                    {secondary.map((row) => (
                                        <StatElement
                                            key={row.id}
                                            title={row.label}
                                            value={row.value.toString()}
                                            icon={row.icon}
                                            onClick={() => openStatModal(row.id)}
                                            hasInfo={row.hasInfo}
                                        />
                                    ))}
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            className={clsx(
                                'w-full',
                                'px-6',
                                'stele:px-24 stele:-translate-y-8'
                            )}
                            variants={drawerVariants}
                            style={{ overflow: 'hidden' }}
                        >
                            <motion.div
                                className={clsx(
                                    'z-10 relative flex flex-col justify-center items-center bg-white-stroke border-white-stroke-darker pattern-background',
                                    'gap-8 p-4 pt-2 border rounded-b-4xl',
                                    'stele:gap-28 stele:p-16 stele:pt-12 stele:border-4 stele:rounded-b-[128px]'
                                )}
                                variants={drawerContentVariants}
                            >
                                <TextDefault
                                    fontSize={isStele ? 48 : 12}
                                    outlineColor='#0A2897'
                                    dropShadowColor='#0A2897'
                                    leading={1}
                                    fontWeight='700'
                                >
                                    Deine Münzen
                                </TextDefault>
                                <div
                                    className={clsx(
                                        'flex justify-center items-center stele:pb-12 w-full',
                                    )}
                                >
                                    <UpgradeBar
                                        unlockedCount={unlockedCount}
                                        barProgress={upgradeBarProgress}
                                        maxUnlocks={MAX_UNLOCKS}
                                        currentCoins={snapshot.runEndStats.coinPowerUpsCollected}
                                    />
                                </div>
                                <div
                                    className={clsx(
                                        'flex flex-col w-full',
                                        'gap-2',
                                        'stele:gap-8'
                                    )}
                                >
                                    {variant === 'gameOver' ? (
                                        <>
                                            {isStele ? (
                                                <Button
                                                    type='button'
                                                    onClick={handleContinueOnPhone}
                                                >
                                                    Spiel auf dem Handy weiter
                                                </Button>
                                            ) : (
                                                <Button
                                                    type='button'
                                                    color='white'
                                                    onClick={handleRestartToConfigurator}
                                                >
                                                    Aussehen ändern
                                                </Button>
                                            )}

                                            <div className='flex gap-1 stele:gap-4'>
                                                <Button
                                                    type='button'
                                                    color='white'
                                                    onClick={handleRestart}
                                                >
                                                    Nochmal versuchen
                                                </Button>
                                                <Button
                                                    type='button'
                                                    color='red'
                                                    onClick={handleReturnToStart}
                                                >
                                                    Beenden
                                                </Button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <Button
                                                type='button'
                                                onClick={handleResume}
                                                icon={<IconRun width='100%' height='100%' />}
                                            >
                                                Weiter Laufen
                                            </Button>
                                            <div className='flex gap-2 w-full'>
                                                <Button
                                                    type='button'
                                                    color='white'
                                                    onClick={handleRestart}
                                                >
                                                    Neu starten
                                                </Button>
                                                <Button
                                                    type='button'
                                                    onClick={handleReturnToStart}
                                                    color='red'
                                                >
                                                    Beenden
                                                </Button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    </motion.div>
                ) : (
                    <StatModal
                        key='stat-modal'
                        content={modalContent}
                        onClose={closeStatModal}
                        onTerminVereinbaren={handleTerminVereinbaren}
                    />
                )}
            </AnimatePresence>
        </div >
    )
})

export default PauseScreen

